/**
 * Card / Panel — surface container.
 *
 * Design system mapping (verified against admin.webex.com `mch-page-section`):
 *   • Background: bg-white dark:bg-webex-gray-950
 *   • Border:     1px solid rgba(0,0,0,0.20) → border-webex-gray-300 dark:border-webex-gray-700
 *   • Radius:     8px → rounded-md
 *   • Shadow:     none (Webex cards are flat)
 *   • Padding:    sm=p-3 md=p-5 lg=p-8 (none = no padding)
 */
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@lib/cn"

export const cardVariants = cva(
  "bg-white dark:bg-webex-gray-950 border border-webex-gray-300 dark:border-webex-gray-700 rounded-md",
  {
    variants: {
      padding: {
        none: "",
        sm: "p-3",
        md: "p-5",
        lg: "p-8",
      },
    },
    defaultVariants: {
      padding: "md",
    },
  },
)

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  as?: keyof React.JSX.IntrinsicElements
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, padding, as: Tag = "div", ...props }, ref) => {
    const Component = Tag as React.ElementType
    return (
      <Component
        ref={ref}
        className={cn(cardVariants({ padding }), className)}
        {...props}
      />
    )
  },
)
Card.displayName = "Card"
